import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react"
import { ChevronDownIcon, EllipsisVerticalIcon } from "@heroicons/react/24/outline"
import React, { Fragment } from "react"
import { twMerge } from "tailwind-merge"

import * as ButtonsModule from "../../components/shared/Buttons"
import ListBoxJs from "../../components/shared/ListBox"
import { DROPDOWN_OPTION_CLASSNAMES } from "../../components/shared/Inputs"
import { useIntakeFormPdfExport } from "../../components/shared/useIntakeFormPdfExport"

type AnyProps = Record<string, unknown> & { children?: React.ReactNode }
const Button = ButtonsModule.Button as unknown as React.FC<AnyProps>
const ListBox = ListBoxJs as unknown as React.FC<AnyProps>

type IntakeFormSummary = {
  id: string | number
  name: string
  isDefault?: boolean
}

type IntakeFormSettingsToolbarProps = {
  intakeForms: IntakeFormSummary[]
  activeFormId: string | number | null | undefined
  multipleIntakeFormsEnabled: boolean
  portalAiAssistantEnabled: boolean
  formName: string
  licenseKey?: string
  getSurveyJson: () => unknown
  onSelectForm: (formId: string) => void
  onNewForm: () => void
  onRename: () => void
  onSetDefault: () => void
  onArchive: () => void
  onGenerateWithAi: () => void
  onUpdateWithAi: () => void
}

function ToolbarMenuDropdown({
  label,
  items,
  buttonType = "secondary",
  align = "right"
}: {
  label: string
  items: { label: string; onClick: () => void; disabled?: boolean }[]
  buttonType?: "secondary" | "tertiary"
  align?: "left" | "right"
}) {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        className={twMerge(
          "inline-flex h-8 items-center gap-1 rounded border px-4 font-bold focus:outline-none focus:ring-2 focus:ring-blue",
          buttonType === "tertiary"
            ? "border-gray bg-white active:bg-gray-light"
            : "border-teal bg-white text-teal active:bg-gray-light"
        )}>
        {label}
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </MenuButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95">
        <MenuItems
          className={twMerge(
            "absolute z-20 mt-2 w-56 origin-top-right rounded-lg border border-gray-light bg-white py-1 shadow-lg focus:outline-none",
            align === "right" ? "right-0" : "left-0"
          )}>
          {items.map((item) => (
            <MenuItem key={item.label} disabled={item.disabled}>
              {({ active }) => (
                <button
                  type="button"
                  disabled={item.disabled}
                  className={twMerge(
                    "block w-full px-3 py-2 text-left text-sm",
                    DROPDOWN_OPTION_CLASSNAMES({ active, selected: false }),
                    item.disabled && "cursor-not-allowed opacity-50"
                  )}
                  onClick={item.onClick}>
                  {item.label}
                </button>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  )
}

export default function IntakeFormSettingsToolbar({
  intakeForms,
  activeFormId,
  multipleIntakeFormsEnabled,
  portalAiAssistantEnabled,
  formName,
  licenseKey,
  getSurveyJson,
  onSelectForm,
  onNewForm,
  onRename,
  onSetDefault,
  onArchive,
  onGenerateWithAi,
  onUpdateWithAi
}: IntakeFormSettingsToolbarProps) {
  const currentForm = intakeForms.find((form) => String(form.id) === String(activeFormId))
  const showFormManagement = multipleIntakeFormsEnabled && intakeForms.length > 0
  const canArchive = showFormManagement && currentForm && !currentForm.isDefault && intakeForms.length > 1
  const canSetDefault = showFormManagement && currentForm && !currentForm.isDefault

  const { busyAction, runExport } = useIntakeFormPdfExport({
    getSurveyJson,
    formName,
    licenseKey,
    surveyJson: undefined,
    responseData: undefined,
    onError: undefined
  })

  const formOptions = intakeForms.map((form) => ({
    value: String(form.id),
    label: form.isDefault ? `${form.name} (Default)` : form.name
  }))

  const selectedFormOption =
    formOptions.find((option) => option.value === String(activeFormId)) ?? formOptions[0] ?? null

  const aiItems = [
    { label: "Generate with AI", onClick: onGenerateWithAi },
    { label: "Update form with AI", onClick: onUpdateWithAi }
  ]

  const exportItems = [
    {
      label: busyAction === "download" ? "Downloading…" : "Download PDF",
      onClick: () => runExport("download"),
      disabled: !!busyAction
    },
    {
      label: busyAction === "print" ? "Preparing…" : "Print",
      onClick: () => runExport("print"),
      disabled: !!busyAction
    }
  ]

  const kebabItems = [
    { label: "Rename", onClick: onRename },
    ...(canSetDefault ? [{ label: "Set as Default", onClick: onSetDefault }] : [])
  ]

  return (
    <div className="my-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {showFormManagement ? (
        <div className="flex min-w-0 flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1 sm:max-w-xs">
            <ListBox
              label="Form"
              options={formOptions}
              value={selectedFormOption}
              onChange={(option: { value: string }) => onSelectForm(option.value)}
            />
          </div>
          {currentForm?.isDefault ? (
            <span className="mb-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              Default
            </span>
          ) : null}
        </div>
      ) : (
        <div className="hidden sm:block" />
      )}

      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        {showFormManagement ? (
          <Button type="primary" onClick={onNewForm}>
            + New Form
          </Button>
        ) : null}

        {portalAiAssistantEnabled ? (
          <ToolbarMenuDropdown label="AI" items={aiItems} buttonType="secondary" />
        ) : null}

        <ToolbarMenuDropdown label="Export" items={exportItems} buttonType="tertiary" />

        {showFormManagement && kebabItems.length > 0 ? (
          <Menu as="div" className="relative inline-block text-left">
            <MenuButton
              className="flex h-8 w-8 items-center justify-center rounded border border-gray bg-white text-gray-dark hover:bg-gray-ultralight focus:outline-none focus:ring-2 focus:ring-blue"
              aria-label="More form actions">
              <EllipsisVerticalIcon className="h-5 w-5" />
            </MenuButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95">
              <MenuItems className="absolute right-0 z-20 mt-2 w-48 origin-top-right rounded-lg border border-gray-light bg-white py-1 shadow-lg focus:outline-none">
                {kebabItems.map((item) => (
                  <MenuItem key={item.label}>
                    {({ active }) => (
                      <button
                        type="button"
                        className={twMerge(
                          "block w-full px-3 py-2 text-left text-sm",
                          DROPDOWN_OPTION_CLASSNAMES({ active, selected: false })
                        )}
                        onClick={item.onClick}>
                        {item.label}
                      </button>
                    )}
                  </MenuItem>
                ))}
              </MenuItems>
            </Transition>
          </Menu>
        ) : null}

        {canArchive ? (
          <Button type="warning" onClick={onArchive}>
            Archive
          </Button>
        ) : null}
      </div>
    </div>
  )
}
